import dayjs, { type Dayjs } from 'dayjs';

export type DATE_FORMAT_RULE_NAME = 'collection' | 'collection-preview' | 'message' | 'session';
type DATE_FORMAT_THRESHOLD = 'today' | 'yesterday' | 'this-week' | 'this-year' | 'other';
const DATE_FORMAT_RULESET_MAP: Record<DATE_FORMAT_RULE_NAME, Partial<Record<DATE_FORMAT_THRESHOLD, string>>> = {
  collection: {
    today: 'common.today',
    yesterday: 'common.yesterday',
    'this-year': '[MM/DD]',
    other: '[YYYY/M/D]',
  },
  'collection-preview': {
    today: 'common.today|[h:mm A]',
    yesterday: 'common.yesterday|[h:mm A]',
    other: '[M/D/YYYY h:mm A]',
  },
  message: {
    today: 'common.today|[HH:mm]',
    yesterday: 'common.yesterday|[HH:mm]',
    'this-year': '[MM-DD HH:mm]',
    other: '[YYYY-MM-DD HH:mm]',
  },
  session: {
    today: '[h:mm A]',
    yesterday: 'common.yesterday',
    'this-week': '[ddd]',
    'this-year': '[M/DD]',
    other: '[YYYY/M/DD]',
  },
};

export function getDateFormatKey(
  timestamp: string | number | Dayjs | Date | undefined | null,
  ruleName: DATE_FORMAT_RULE_NAME,
  t: (key: string, params?: Record<string, unknown>) => string,
): string | null {
  const date = dayjs(timestamp);
  const ruleset = DATE_FORMAT_RULESET_MAP[ruleName];
  const threshold = getDateFormatThreshold(date, ruleset);

  if (ruleset?.[threshold]) {
    return ruleset[threshold]
      .split('|')
      .map((part) => {
        if (part.startsWith('[')) {
          const format = part.slice(1, -1);
          if (format.includes('A')) {
            return dayjs(date).locale('en').format(format);
          }
          return dayjs(date).format(format);
        }
        return t(part);
      })
      .join(' ');
  }

  return null;
}

export function getDateFormatThreshold(
  timestamp: string | number | Dayjs | Date | undefined | null,
  ruleset?: Partial<Record<DATE_FORMAT_THRESHOLD, string>>,
): DATE_FORMAT_THRESHOLD {
  const date = dayjs(timestamp);
  const nowDate = dayjs();

  if (!ruleset) return 'other';

  if ('today' in ruleset && date.isSame(nowDate, 'day')) {
    return 'today';
  }
  if ('yesterday' in ruleset && date.isSame(nowDate.subtract(1, 'day'), 'day')) {
    return 'yesterday';
  }
  if ('this-week' in ruleset && date.isAfter(nowDate.subtract(7, 'days'), 'day')) {
    return 'this-week';
  }
  if ('this-year' in ruleset && date.isSame(nowDate, 'year')) {
    return 'this-year';
  }

  return 'other';
}

export function getDurationFormatKey(value: number, withUnits = true, compact = false): string {
  const duration = dayjs.duration(value, 'seconds');

  if (withUnits) {
    if (value <= 120) {
      return `common.second|count=${value}`;
    }
    if (duration.days() > 0) {
      return `common.day|count=${duration.days()}`;
    }
    if (duration.hours() > 0) {
      return `common.hour|count=${duration.hours()}`;
    }
    return `common.minute|count=${duration.minutes()}`;
  }

  if (duration.days() > 0) {
    return `||${duration.format(compact ? 'D' : 'DD')}`;
  }
  if (duration.hours() > 0) {
    return `||${duration.format(compact ? 'H:m:ss' : 'HH:mm:ss')}`;
  }

  return `||${duration.format(compact ? 'm:ss' : 'mm:ss')}`;
}
